import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormGroup,
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ScreenType } from 'src/app/enums/enums';
import { NavigationService } from 'src/app/services/navigation.service';
import { DeliveryCourierFormData } from 'src/app/models/delivery-courier/delivery-courier-form-data.model';
import { FormService } from 'src/app/services/form.service';
import { ErrorMsg } from 'src/app/models/error/error-msg.model';
import { ErrorResponse } from 'src/app/models/error/error-response.model';
import { SubmitButtonComponent } from '../../../common/submit-button/submit-button.component';
import { BackButtonComponent } from '../../../common/back-button/back-button.component';
import { FormInputControlComponent } from '../../../common/form-input-control/form-input-control.component';
import { ApiService } from 'src/app/services/api.service';
import { firstValueFrom } from 'rxjs';
import {
  emailValidator,
  phoneValidator,
} from 'src/app/common/validatos/validators';
import { CaptchaDialogService } from 'src/app/services/captcha-dialog.service';
import { FormErrorMessageComponent } from '../../../common/form-error-message/form-error-message.component';
import { StoreService } from 'src/app/services/store.service';

@Component({
  selector: 'app-delivery-courier-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SubmitButtonComponent,
    BackButtonComponent,
    FormInputControlComponent,
    FormErrorMessageComponent,
  ],
  templateUrl: './delivery-courier-form.component.html',
  styleUrl: './delivery-courier-form.component.scss',
})
export class DeliveryCourierFormComponent implements AfterViewInit {
  ScreenType = ScreenType;

  courierForm!: FormGroup;
  submitException = signal(false);
  isSubmitLoading = signal(false);

  constructor(
    private apiService: ApiService,
    private navigationService: NavigationService,
    private fb: FormBuilder,
    public formService: FormService,
    private cdr: ChangeDetectorRef,
    private captchaDialogService: CaptchaDialogService,
    protected storeService: StoreService
  ) {
    this.initializeForm();
  }

  private initializeForm() {
    const formData = this.storeService.getDeliveryCourierFormData();

    this.courierForm = this.fb.group({
      name: [formData?.name, [Validators.required]],
      company: [formData?.company],
      zip_code: [
        formData?.zipCode,
        [Validators.required, Validators.pattern('^[0-9]{2}-[0-9]{3}$')],
      ],
      city: [formData?.city, [Validators.required]],
      street: [formData?.street, [Validators.required]],
      house_number: [formData?.houseNumber, [Validators.required]],
      apartment_number: [formData?.apartmentNumber],
      phone: [formData?.phone, [Validators.required, phoneValidator()]],
      email: [formData?.email, [Validators.required, emailValidator()]],
    });

    this.formService.markAllNotEmptyAsTouched(this.courierForm);
    this.courierForm.valueChanges.subscribe(() => this.saveFormDataToCache());
    this.zipCode?.valueChanges.subscribe(() => {
      this.city?.updateValueAndValidity({ emitEvent: false });
    });

    this.city?.valueChanges.subscribe(() => {
      this.zipCode?.updateValueAndValidity({ emitEvent: false });
    });
  }

  get name() {
    return this.courierForm.get('name');
  }
  get company() {
    return this.courierForm.get('company');
  }
  get zipCode() {
    return this.courierForm.get('zip_code');
  }
  get city() {
    return this.courierForm.get('city');
  }
  get street() {
    return this.courierForm.get('street');
  }
  get houseNumber() {
    return this.courierForm.get('house_number');
  }
  get apartmentNumber() {
    return this.courierForm.get('apartment_number');
  }
  get phone() {
    return this.courierForm.get('phone');
  }
  get email() {
    return this.courierForm.get('email');
  }

  ngAfterViewInit() {
    this.handleFormErrors();
  }

  private handleFormErrors() {
    const errorResponse = this.storeService.getErrorResponse();
    let errorMsgCopy: ErrorMsg[] | undefined = undefined;

    if (errorResponse) {
      errorMsgCopy = errorResponse?.errorMsg?.filter((err) => {
        let control: any;

        if (err.idRow === 2) {
          switch (err.field) {
            case 'name':
              control = this.name;
              break;
            case 'companyName':
              control = this.company;
              break;
            case 'city':
              control = this.city;
              break;
            case 'postalCode':
              control = this.zipCode;
              break;
            case 'address1':
              control = this.street;
              break;
            case 'phone':
              control = this.phone;
              break;
            case 'email':
              control = this.email;
              break;
            default:
              control = null;
          }

          if (control) {
            control.setErrors({ [err.errorCode]: true });
            control.markAsTouched();
            return false; // Usuwa err z listy errorMsg
          }
        }

        return true; // Zatrzymuje err w liście errorMsg
      });
    }

    const errorRespCpy: ErrorResponse = {
      httpStatus: errorResponse?.httpStatus,
      errorMsg: errorMsgCopy,
    };

    this.storeService.setPickupErrorResponse(errorRespCpy);
    this.cdr.detectChanges();
  }

  saveFormDataToCache(): void {
    const deliveryCourierFormData: DeliveryCourierFormData = {
      name: this.name?.value,
      company: this.company?.value,
      zipCode: this.zipCode?.value,
      city: this.city?.value,
      street: this.street?.value,
      houseNumber: this.houseNumber?.value,
      apartmentNumber: this.apartmentNumber?.value,
      phone: this.phone?.value,
      email: this.email?.value,
    };

    this.storeService.setDeliveryPointFormData(undefined);
    this.storeService.setDeliveryCourierFormData(deliveryCourierFormData);
  }

  backButtonAction() {
    this.storeService.setSelectedPartners([]);
    this.navigationService.changeScreenTo(ScreenType.UTWORZ_PRZESYLKE);
  }

  async onSubmit() {
    this.isSubmitLoading.set(true);

    if (this.courierForm.invalid) {
      this.formService.markAllAsTouched(this.courierForm);
      this.isSubmitLoading.set(false);
      return;
    }

    this.saveFormDataToCache();

    try {
      const cityZipCodeResponse = await firstValueFrom(
        this.apiService.submitCityZipCodeRequest({
          zipCode: this.zipCode?.value,
          city: this.city?.value,
        })
      );
      this.submitException.set(false);
      this.storeService.setSelectedPartners([]);
      this.navigationService.changeScreenTo(ScreenType.OPCJE_NADANIA);
    } catch (error) {
      this.handleCityZipCodeError(error);
      return;
    } finally {
      this.isSubmitLoading.set(false);
    }
  }

  handleCityZipCodeError(error: any) {
    try {
      const errorResponse: ErrorResponse = {
        httpStatus: error.status,
        errorMsg: error.error.errorMsg.map((msg: any) => ({
          errorCode: msg.errorCode,
          field: msg.field,
          idRow: msg.idRow,
          errorMsgLabel: msg.errorMsgLabel,
          errorMsgArgs: msg.errorMsgArgs,
        })),
      };
      if (errorResponse?.errorMsg?.[0]?.errorCode === 'FORBIDDEN') {
        this.captchaDialogService.openCaptchaDialog();
      } else if (errorResponse?.errorMsg?.[0]?.errorCode === 'INCONSISTENT') {
        this.zipCode?.setErrors({ zip_code_city_inconsistent: true });
        this.city?.setErrors({ zip_code_city_inconsistent: true });
      } else {
        this.submitException.set(true);
      }
    } catch (error) {
      this.submitException.set(true);
    }
  }
}
